import { Component } from '@theme/component';
import { debounce, isClickedOutside, isMobileBreakpoint, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * Dialogs whose `<dialog>` element carries a `mobile-sheet` attribute become
 * swipe-to-dismiss bottom sheets on mobile (drag the top handle/header zone
 * down to close) — the same opt-in mechanism used by both the cart drawer and
 * the "more" menu drawer, so any future bottom sheet gets this for free
 * without duplicating the gesture logic.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  /** @type {{ startY: number, lastY: number } | null} */
  #dragState = null;

  /** Only pointerdown events starting within this many px of the dialog's top count as the handle/header zone. */
  #dragHandleZone = 56;

  #snapBackDuration = 220;

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }

    this.addEventListener('pointerdown', this.#handleDragStart);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    this.removeEventListener('pointerdown', this.#handleDragStart);
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Starts tracking a drag when the pointer goes down within the handle/header
   * zone of a `mobile-sheet` dialog on mobile. No-ops for dialogs that haven't
   * opted in, or on desktop, where drawers/modals keep their normal behavior.
   *
   * @param {PointerEvent} event
   */
  #handleDragStart = (event) => {
    const { dialog } = this.refs;
    if (!dialog?.hasAttribute('mobile-sheet')) return;
    if (!isMobileBreakpoint()) return;
    if (!dialog.open) return;
    if (event.target instanceof Element && event.target.closest('button, a, input, textarea, select')) return;

    const rect = dialog.getBoundingClientRect();
    if (event.clientY - rect.top > this.#dragHandleZone) return;

    this.#dragState = { startY: event.clientY, lastY: event.clientY };

    // Neutralize the open animation's fill-forwards transform so the inline
    // transform set during the drag isn't overridden (same trick used in closeDialog).
    dialog.style.animation = 'none';
    void dialog.offsetWidth;

    window.addEventListener('pointermove', this.#handleDragMove);
    window.addEventListener('pointerup', this.#handleDragEnd);
    window.addEventListener('pointercancel', this.#handleDragEnd);
  };

  /** @param {PointerEvent} event */
  #handleDragMove = (event) => {
    if (!this.#dragState) return;

    const { dialog } = this.refs;
    const deltaY = Math.max(0, event.clientY - this.#dragState.startY);
    this.#dragState.lastY = event.clientY;
    dialog.style.transform = `translateY(${deltaY}px)`;
    event.preventDefault();
  };

  #handleDragEnd = () => {
    if (!this.#dragState) return;

    const { dialog } = this.refs;
    const deltaY = Math.max(0, this.#dragState.lastY - this.#dragState.startY);
    const dismissThreshold = dialog.getBoundingClientRect().height * 0.2;
    const shouldDismiss = deltaY > dismissThreshold;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    window.removeEventListener('pointermove', this.#handleDragMove);
    window.removeEventListener('pointerup', this.#handleDragEnd);
    window.removeEventListener('pointercancel', this.#handleDragEnd);
    this.#dragState = null;

    if (shouldDismiss) {
      dialog.style.transform = '';
      dialog.style.animation = '';
      this.closeDialog();
      return;
    }

    if (reducedMotion || deltaY === 0) {
      dialog.style.transform = '';
      dialog.style.animation = '';
      return;
    }

    dialog.style.transition = `transform ${this.#snapBackDuration}ms cubic-bezier(0.22, 1, 0.36, 1)`;
    dialog.style.transform = 'translateY(0)';
    dialog.addEventListener(
      'transitionend',
      () => {
        dialog.style.transition = '';
        dialog.style.transform = '';
        dialog.style.animation = '';
      },
      { once: true }
    );
  };

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
